import { Box, Typography } from "@mui/material";
import Stack from "@mui/material/Stack";
import { useNavigate, useParams } from "react-router-dom";

import { getListQuote } from "../../../api/quote/getListQuote";
import { getVehicleImages } from "../../../api/vehicle/getVehicleImages";
import { getVehicleOptions } from "../../../api/vehicle/getVehicleOptions";
import { getVehicleVariant } from "../../../api/vehicle/getVehicleVariant";
import APInfoText from "../../../components/APInfoText";
import APButton from "../../../components/buttons/APButton";
import APContactHelpButton from "../../../components/buttons/APContactHelpButton";
import BackButton from "../../../components/buttons/BackButton";
import HomeButton from "../../../components/buttons/HomeButton";
import PageContainer from "../../../components/PageContainer";
import RestrictedAppScope from "../../../components/RestrictedAppScope";
import { useFaqs } from "../../../hooks/useFaqs";
import { useVehicleSourcingAccess } from "../../../hooks/useVehicleSourcingAccess";
import { useAuth } from "../../../providers/AuthProvider";
import APVehicleDetail from "../../vehicleDetail/components/VehicleDetail";

const VehicleSourcingConfirmVehicle = () => {
  const { dossierId } = useParams();
  const navigate = useNavigate();
  const { getScopedPageUrl } = useAuth();

  if (!dossierId) {
    // TODO: improve client side error messaging
    throw new Error("dossier id param is misisng");
  }

  const {
    dossier,
    navigationCheckComplete,
    isLoading: isDossierLoading,
    isSuccess: isDossierSuccess,
  } = useVehicleSourcingAccess({ dossierId });

  const primaryQuoteId = dossier.data?.opportunity?.primaryQuoteId ?? null;
  const primaryQuote = getListQuote(primaryQuoteId ?? "", {
    disabled: !primaryQuoteId,
  });
  const vehicleVariant = getVehicleVariant(
    primaryQuote.data?.vehicle.variantCode ?? "",
    { disabled: !primaryQuote.data }
  );
  const vehicleImages = getVehicleImages(
    primaryQuote.data?.vehicle.variantCode ?? "",
    { disabled: !primaryQuote.data }
  );
  const vehicleOptions = getVehicleOptions(
    primaryQuote.data?.vehicle.variantId ?? "",
    { disabled: !primaryQuote.data }
  );

  const {
    faqProps,
    isLoading: isLoadingFaqs,
    isSuccess: isSuccessFaqs,
  } = useFaqs({
    pageId: "confirm-vehicle-to-source",
    contextIds: {
      opportunityId: dossier?.data?.opportunity?.id,
    },
  });

  const isLoading =
    isDossierLoading ||
    primaryQuote.isLoading ||
    vehicleVariant.isLoading ||
    vehicleImages.isLoading ||
    vehicleOptions.isLoading ||
    isLoadingFaqs;
  const isSuccess =
    isDossierSuccess &&
    primaryQuote.isSuccess &&
    vehicleVariant.isSuccess &&
    vehicleImages.isSuccess &&
    vehicleOptions.isSuccess &&
    isSuccessFaqs;

  const driverSourcedVehicle =
    dossier.data?.opportunity?.driverSourcedVehicle ?? false;

  const onChangeVehicle = () => {
    navigate(
      getScopedPageUrl("dossierVehicleSourcingSelectVehicle", dossierId)
    );
  };

  const renderChangeVehicleButton = () => {
    return (
      <APButton
        text="Change"
        onClick={onChangeVehicle}
        icon="car"
        variant="contained"
      />
    );
  };

  const topText = driverSourcedVehicle
    ? "Please confirm the vehicle."
    : "Please confirm the vehicle so we can obtain pricing and availability.";

  const renderAppLocation = faqProps.renderAppLocation;

  return (
    <PageContainer
      loading={isLoading || !navigationCheckComplete}
      title="Confirm Vehicle"
      titleButton={<Box>{renderChangeVehicleButton()}</Box>}
    >
      {isSuccess && navigationCheckComplete && (
        <Stack spacing={2}>
          <Typography variant="labelLarge">{topText}</Typography>

          <APVehicleDetail
            vehicleVariant={vehicleVariant.data}
            vehicleImages={vehicleImages.data}
            vehicleOptions={vehicleOptions.data}
            shouldPromptForAIConsent={false}
            onAISuggestions={() => {}}
            onAIReview={() => {}}
            priceToBeConfirmed={true}
            displaySections={{
              images: true,
              specs: true,
              details: true,
              options: false,
              manufacturerOptions: false,
              standardFeatures: true,
              aiReview: false,
              aiSuggestions: false,
            }}
            hideExpandCollapseAllButton={true}
            slots={{
              besideSpecs: (
                <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
                  {renderChangeVehicleButton()}
                </Box>
              ),
            }}
          />

          {!driverSourcedVehicle &&
            renderAppLocation(
              "ConfirmVehicleToSource-SelectVehicleText",
              <APInfoText
                text={`Please ensure you select the correct vehicle as this will affect pricing and availability.\n Click Next to confirm vehicle.`}
              />
            )}

          <Stack
            spacing={2}
            sx={{
              display: "flex",
              flexDirection: "column",
              pt: 2,
            }}
          >
            <Box
              sx={{
                display: "flex",
                columnGap: 1,
                justifyContent: "space-between",
              }}
            >
              <Box>
                <Box
                  sx={{ display: "flex", columnGap: 1, alignItems: "center" }}
                >
                  <BackButton />
                  <HomeButton destination="dossiersHome" />
                </Box>
              </Box>
              <Box>
                <APButton
                  text="Next"
                  variant="contained"
                  color="secondary"
                  onClick={() => {
                    navigate(
                      getScopedPageUrl(
                        "dossierVehicleSourcingOptions",
                        dossierId
                      )
                    );
                  }}
                  icon="play"
                />
              </Box>
            </Box>
            <Box
              sx={{
                display: "flex",
                columnGap: 1,
                justifyContent: "space-between",
              }}
            >
              <RestrictedAppScope scope="external">
                <APContactHelpButton
                  regardingOpportunityId={
                    dossier.data?.opportunity?.id ?? undefined
                  }
                />
              </RestrictedAppScope>
              <Box>{renderChangeVehicleButton()}</Box>
            </Box>
          </Stack>
        </Stack>
      )}
    </PageContainer>
  );
};

export default VehicleSourcingConfirmVehicle;
